import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { answerText } from "@/components/groups/GroupQuestionsManager";

const PAGE = 10;
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

/** Member-centric answer review (Manage → Members). Reads the SAME canonical
 *  membership-answer store as Manage → Questions → Answers, Owner/Admin only, one
 *  bounded page of that member's submission history (GRP-DEC-311). */
export function GroupMemberAnswers({ groupId, userId }) {
  const [offset, setOffset] = React.useState(0);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["group-member-answers", groupId, userId, offset],
    queryFn: async () => (await api.get(
      `/groups/${groupId}/members/${userId}/answers`,
      { params: { limit: PAGE, offset } })).data,
    retry: false,
  });
  const rows = data?.submissions || [];
  const total = data?.total || 0;

  if (isLoading) {
    return <p className="w-full text-sm text-muted-foreground">Loading answers…</p>;
  }
  if (isError) {
    return <p className="w-full text-sm text-muted-foreground">Couldn't load answers.</p>;
  }
  if (!rows.length) {
    return (
      <p className="w-full text-sm text-muted-foreground"
         data-testid={`member-answers-empty-${userId}`}>
        This member joined without answering any membership questions.
      </p>
    );
  }
  return (
    <div className="w-full space-y-2" data-testid={`member-answers-${userId}`}>
      {rows.map((s) => (
        <div key={s.id} className="space-y-1 rounded-lg border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">v{s.questions_version}</Badge>
            <span className="text-xs text-muted-foreground">
              via {String(s.via || "join").replace(/_/g, " ")} · submitted {fmt(s.submitted_at)}
              {s.period_index > 0 ? ` · membership period ${s.period_index + 1}` : ""}
            </span>
          </div>
          {(s.answers || []).map((a) => (
            <p key={a.question_id} className="text-sm">
              <span className="font-medium">{a.label}: </span>
              <span className="text-muted-foreground">{answerText(a.value)}</span>
            </p>
          ))}
        </div>
      ))}
      {total > PAGE ? (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                  data-testid={`member-answers-prev-${userId}`}>Previous</Button>
          <span className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Button variant="outline" size="sm" disabled={offset + PAGE >= total}
                  onClick={() => setOffset(offset + PAGE)}
                  data-testid={`member-answers-next-${userId}`}>Next</Button>
        </div>
      ) : null}
    </div>
  );
}
